import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { t } from "i18next";
import { useRouter } from "@src/lib/router";
import { handleStartDownloadContent } from "../../helpers/handleDownloadContent";
import styles from "../../styles/ContentActionMenu.module.scss";
import { markContentAsDoneAction, patchContentMarkAsDoneAction } from "../../store/actions";
import isContentDownloadable from "../../helpers/isContentDownloadable";
import isVideoExhausted from "../../helpers/isVideoExhausted";
import { CONTENT_DOWNLOAD_STATUS } from "../../store/constants";
import { getOrgId } from "@src/cosmosTracking/helper";
import ContentDownloader from "./ContentDownloader";

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#7A8B94" />
    <circle cx="12" cy="7.25" r="1.75" fill="#fff" />
    <circle cx="12" cy="12" r="1.75" fill="#fff" />
    <circle cx="12" cy="16.75" r="1.75" fill="#fff" />
  </svg>
);

const DownloadCircleIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10 6.25V12.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M7.25 10.5L10 13.25L12.75 10.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CompleteCircleIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M6.75 10.25L8.75 12.25L13.25 7.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UnmarkedCircleIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M7 7L13 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M13 7L7 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ContentActionMenu = ({
  content,
  getOfflineDownloadedItemState,
  handleSetCheckProgressInterval,
  isContentMarkedDone = false,
  isCourseContentMarkedExists = false,
  payloadData = {},
  handleGetOfflineDownloadList,
  getOfflineDownloadedItemProgress,
  handleClearCheckProgressInterval,
  isAttachment = false,
  showDownloadForPdf = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const router = useRouter();
  const courseName = useSelector(
    (state) => state?.overviewReducer?.overViewDetails?.course?.details?.name
  );

  const contetTypeValue = {
    2: "video",
    3: "document",
    5: "liveClasses",
  }

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = (e) => {
    e?.stopPropagation();
    setIsOpen((open) => !open);
  };

  const downloadState = getOfflineDownloadedItemState?.(content);
  const showDownloadIcon = () => {
    if (isNaN(downloadState)) return true;
    switch (downloadState) {
      case CONTENT_DOWNLOAD_STATUS.DOWNLOAD_PAUSE:
      case CONTENT_DOWNLOAD_STATUS.DOWNLOAD_FAILED:
      case CONTENT_DOWNLOAD_STATUS.DOWNLOAD_REMOVE:
      case CONTENT_DOWNLOAD_STATUS.DOWNLOAD_CANCEL:
      case CONTENT_DOWNLOAD_STATUS.DOWNLOAD_UNKNOWN:
        return true;
      default:
        return false;
    }
  };

  const deviceType = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    ? "IOS"
    : "ANDROID";
  const checkIfDrmVideoInIoS =
    deviceType === "IOS" &&
    content?.drmProtected === 1 &&
    ![379965, 170, 573212].includes(getOrgId());

  const canShowDownload =
    isContentDownloadable(content) &&
    !isVideoExhausted(content) &&
    !content.isLocked &&
    showDownloadIcon() &&
    !content?.isAudio &&
    !checkIfDrmVideoInIoS;

  const handleDownload = (e) => {
    e?.stopPropagation();
    handleStartDownloadContent({
      ...content,
      courseId: +router?.query?.courseId,
      originalCourseId: parseInt(router?.query?.originalCourseId),
      courseName,
    });
    handleSetCheckProgressInterval?.();
    setIsOpen(false);
  };

  const handleMarkAsComplete = (e) => {
    e?.stopPropagation();
    if(payloadData.contentId === undefined) {
      payloadData.contentId = content?.id || content?.contentId;
    }
    if(payloadData.courseId === undefined) {
      payloadData.courseId = +router?.query?.courseId;
    }
    if(isCourseContentMarkedExists) {
      dispatch(patchContentMarkAsDoneAction({...payloadData, type: contetTypeValue[content?.contentType], markAsDone: isContentMarkedDone ? false : true}))
    } else {
      dispatch(markContentAsDoneAction({ courseId: +router?.query?.courseId, contentId: isAttachment ? content?.contentId || content?.id : content?.id, type: contetTypeValue[content?.contentType] }));
    }
    
    setIsOpen(false);
  };

  const customIcons = () => {
    return (
      <button
        type="button"
        className={styles.contentActionMenu__item}
        onClick={handleDownload}
        role="menuitem"
      >
        <span className={styles.contentActionMenu__itemIcon}>
          <DownloadCircleIcon />
        </span>
        {t(
          "components.tabs.content.components.contentList.listItem.downloadText",
          "Download"
        )}
     </button>
  ) 
    
  }

  return (
    <div
      className={`${styles.contentActionMenu} ${
        isOpen ? styles["contentActionMenu--open"] : ""
      }`}
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
    >
      {isOpen ? (
        <div className={styles.contentActionMenu__popup} role="menu">
          {isContentDownloadable(content) &&
                !isVideoExhausted(content) && !isAttachment &&
                !content.isLocked && (
                  <ContentDownloader
                    content={content}
                    handleGetOfflineDownloadList={handleGetOfflineDownloadList}
                    getOfflineDownloadedItemProgress={
                      getOfflineDownloadedItemProgress
                    }
                    getOfflineDownloadedItemState={getOfflineDownloadedItemState}
                    handleSetCheckProgressInterval={handleSetCheckProgressInterval}
                    handleClearCheckProgressInterval={
                      handleClearCheckProgressInterval
                    }
                    customIcons={customIcons}
                    isCustomIcons={true}
                  />
                )}

                {content?.canDownload === 1 && isAttachment && <ContentDownloader
                    content={content}
                    handleGetOfflineDownloadList={handleGetOfflineDownloadList}
                    getOfflineDownloadedItemProgress={
                      getOfflineDownloadedItemProgress
                    }
                    getOfflineDownloadedItemState={getOfflineDownloadedItemState}
                    handleSetCheckProgressInterval={handleSetCheckProgressInterval}
                    handleClearCheckProgressInterval={
                      handleClearCheckProgressInterval
                    }
                    customIcons={customIcons}
                    isCustomIcons={true}
                  />}


          <button
            type="button"
            className={styles.contentActionMenu__item}
            onClick={handleMarkAsComplete}
            role="menuitem"
          >
            <span className={styles.contentActionMenu__itemIcon}>
              {isContentMarkedDone ? <UnmarkedCircleIcon /> : <CompleteCircleIcon />}
            </span>
            {isContentMarkedDone ?t(
              "components.tabs.content.components.contentList.listItem.markAsUncompleteText",
              "Mark as Uncompleted"
            ) : t(
              "components.tabs.content.components.contentList.listItem.markedAsCompleteText",
              "Mark as Complete"
            ) }
          </button>
        </div>
      ) : null}
      <button
        type="button"
        className={styles.contentActionMenu__trigger}
        onClick={handleToggle}
        aria-label="More actions"
        aria-expanded={isOpen}
      >
        <MoreIcon />
      </button>
    </div>
  );
};

export default ContentActionMenu;
